import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BucketS3Service } from '@shared/services/bucket-s3.service';
import { PricingService } from '@shared/services/pricing.service';
import { FunctionsService } from '@shared/services/functions.service';
import { SeoMetadataService } from '@shared/services/seo-metadata.service';
import { CartApiService } from '@modules/cart/cart-api.service';
import { Store } from '@ngrx/store';
import { AppState } from '@shared/redux/config-store-state.model';
import { emitAlert, basepriceAction } from '@shared/redux/actions/actions-store-state.model';
import { CartStorageService, CartStorage, BookMerge } from '@shared/services/cart-storage.service';
import { configAction, updateUser } from '@shared/redux/actions/actions-store-state.model';
import { TranslateService } from '@ngx-translate/core';
import { AuthFirebaseService } from '@shared/services/auth-firebase.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'astrea-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss']
})
export class CartComponent implements OnInit, OnDestroy {

  public isLoggedIn: boolean = false;
  private auth$!: Subscription;

  public loading = true;
  public currentTab = 0;

  public user!: any;
  private subsUser$!: Subscription;

  public userInvoicing = false;
  public userResidence = false;

  // Información del carrito de cada obra
  public bookStorage: CartStorage[] | null = [];

  // Información traida del backend de cada obra
  public booksDetails: any[] = [];

  // Información del backend + carrito de cada obra
  public booksMerge: BookMerge[] = [];

  // Obras físicas y digitales por separado
  public physicalBooks: BookMerge[] = [];
  public digitalBooks: BookMerge[] = [];

  constructor(
    public S3Service: BucketS3Service,
    public fnService: FunctionsService,
    public pricingService: PricingService,
    private router: Router,
    private store: Store<AppState>,
    private seo: SeoMetadataService,
    private api: CartApiService,
    private cartStorageService: CartStorageService,
    private authFirebaseService: AuthFirebaseService,
    private _t: TranslateService,
  ) { }

  ngOnInit(): void {
    this.setMetadata();
    this.getCartStorage();
    this.userIsLoggedIn();
    this.getUserInfo();
  }

  ngOnDestroy(): void {
    if (this.auth$) this.auth$.unsubscribe();
    if (this.subsUser$) this.subsUser$.unsubscribe();
  }

  setMetadata(): void {
    this.seo.setMetadata({
      title: `Comprar obras | Editorial Astrea`,
      description: `Página de confirmación de compras.`
    });
  }

  getUserInfo(): void {
    this.subsUser$ = this.store.select(state => state.storeUser)
      .subscribe({
        next: userData => {
          this.user = userData.user;
          if (this.user)
            this.checkUserData();
          else if (this.isLoggedIn)
            this.getUserIfNotExist();
        }
      });
  }

  getUserIfNotExist(): void {
    this.api.getUser()
      .subscribe({
        next: res => {
          this.store.dispatch(updateUser({ user: res.result.data }));
          this.store.dispatch(configAction({
            darkMode: res.result.data.darkmode,
            language: res.result.data.language,
            currency: res.result.data.currency
          }));
        },
        error: () => {
          /*
            Si el usuario no existe, queda la sesión abierta.
            Verificar acá, en ProfileComponent & AppConfigService
          */
        }
      });
  }

  checkUserData() {
    // Validamos que todos los campos estén completos dependiendo el país
    if (
      this.user && this.user.businessName && this.user.cuit && this.user.address && this.user.postalCode && this.user.city &&
      ((this.user.country == 'AR' && this.user.state && this.user.ivaCondition) || (this.user.country && this.user.country != 'AR'))
    ) {
      this.userInvoicing = true;
    }

    if (
      this.user && this.user.firstname && this.user.lastname && this.user.addressResidence && this.user.postalCodeResidence && this.user.cityResidence &&
      ((this.user.nationality == 'AR' && this.user.provinceResidence) || (this.user.nationality && this.user.nationality != 'AR'))
    ) {
      this.userResidence = true;
    }

    if (this.user && this.user.nationality && this.user.nationality != 'AR')
      this.getUserShipment();
  }

  getCartStorage(): void {
    this.bookStorage = this.cartStorageService.getCartStorage();
    if (this.bookStorage)
      this.getCartStorageDetails();
    else
      this.loading = false;
  }

  getCartStorageDetails(): void {
    this.api.getBooks(this.bookStorage)
      .subscribe({
        next: res => {
          this.booksDetails = res.result.data;
          this.mergeDataBooks();
          this.loading = false;
        },
        error: () => {
          this.cartStorageService.resetCart();
          this.alertResetCart();
          this.loading = false;
        }
      });
  }

  getUserShipment(): void {
    const cartInfo = this.bookStorage ? this.bookStorage.filter(c => c.typeStorage == 'physical_book') : [];
    if (cartInfo.length > 0) {
      this.api.getUserShipment(cartInfo)
        .subscribe({
          next: res => {
            if (res.result.data) {
              const baseprice = res.result.data?.baseprice;
              if (baseprice)
                this.store.dispatch(basepriceAction({ baseprice: baseprice }));
            }
          }
        });
    }
  }

  mergeDataBooks(): void {
    let booksMerge: BookMerge[] = [];

    // Match entre la información del backend y la del carrito
    this.bookStorage?.map((cart: CartStorage) => {
      let bookMerge: BookMerge = {
        bookInfo: null,
        cartInfo: cart
      };
      this.booksDetails.forEach((bd: any) => {
        if (bd.bookcode == cart.bookcode)
          bookMerge.bookInfo = bd;
      });
      booksMerge.push(bookMerge);
    });

    // Aseguramos que no haya items rotos, los removemos si por algún motivo el backend no encontró un bookcode
    booksMerge = booksMerge.filter(b => {
      if (!b.bookInfo) {
        this.bookStorage = this.cartStorageService.delCartStorage(b.cartInfo.bookcode, b.cartInfo.typeStorage);
        return false;
      } else
        return true;
    });

    this.booksMerge = booksMerge;
    this.splitBooks();
  }

  splitBooks(): void {
    this.physicalBooks = [];
    this.digitalBooks = [];
    this.booksMerge.forEach((b: BookMerge) => {
      if (b.cartInfo.typeStorage == 'physical_book') {
        this.physicalBooks.push(b);
      }
      else if (b.cartInfo.typeStorage == 'digital_book') {
        this.digitalBooks.push(b);
      }
    });
  }

  userIsLoggedIn(): void {
    this.auth$ = this.authFirebaseService.isLoggedIn()
      .subscribe({
        next: user => {
          this.isLoggedIn = user === null ? false : true;
        }
      })
  }

  handleUpdateItem(updateItem: any): void {
    this.bookStorage = this.cartStorageService.updCartStorage(updateItem.bookcode, updateItem.typeStorage, updateItem);
    this.mergeDataBooks();

    // Actualizamos el valor del envío
    if (this.user && this.user.nationality && this.user.nationality != 'AR')
      this.getUserShipment();
  }

  handleDeleteItem(deleteItem: any): void {
    this.bookStorage = this.cartStorageService.delCartStorage(deleteItem.bookcode, deleteItem.typeStorage);
    this.mergeDataBooks();
  }

  handleProceedToPurchase(): void {
    if (this.isLoggedIn) {
      this.handleChangeTab(1);
    } else {
      this.router.navigate(['/login'], { queryParams: { redirect: `cart` } });
    }
  }

  handleChangeTab(tab: number): void {
    this.currentTab = tab;
  }

  alertResetCart(): void {
    this.store.dispatch(emitAlert({
      message: this._t.instant('alert.cart_err'),
      alertType: 'danger'
    }));
  }

}
